import { ChartOptions, ChartType } from 'chart.js';
import {
  attachChartTooltipWcag1413,
  chartTooltipApplyChartHide,
  chartTooltipHideImmediately,
  chartTooltipOnShown
} from 'src/app/core/utils/chart-tooltip-accessibility';
import { ActivityPriorityType } from 'src/app/core/services/http/activity-priority/enums/activity-priority-type';
import { AppRoute } from '../constants/app.route';
import { Authority } from '../constants/authority';
import { Status } from '../core/services/http/general/enums/status';
import { StudentAttendanceType } from '../core/services/http/student-attendance/enums/student-attendance-type';
import { BulkType } from '../features/schedule/schedule-dates-modal/bulk-dates-form/enums/bulk-type';
import { CalendarViewTab } from '../shared/date-picker/enums/calendar-view-tab';

export const AppConfig = {
  Api: { maxUploadFileSizeMB: 50, pageSize: 50, pageSizeAll: 1000, headerXTotalName: 'x-pagination-total-count' },
  AutoComplete: { minTermLength: 2, debounceTimeMs: 500, clearTimeOut: 4000, pageSize: 100 },
  Preloader: { debounceTimeMs: 400 },
  Inputs: { allowedSpecialCharacters: '', minTermLength: 2 },
  Datatable: {
    nullableDatatableCellSumbol: '-',
    defaultColumnWidth: 100
  },
  DropDown: { ActivityTypeAllowTreeLevel: 1 },
  Language: { defaultLanguage: 'en', defaultLocale: 'en-US' },
  Calendar: { switcherTodayPlusDaysCount: 4, intervalUpdate: 30000 },
  Alert: { dismissOnTimeout: 7000 },
  GraduationYearMinusLimitInYears: 1,
  GraduationYearPlusLimitInYears: 14,
  Features: {
    notifications: true
  },
  Formats: {
    MonthDayFormat: 'MMM dd',
    DateApiFormat: 'Y-MM-dd',
    DateApiFormatFull: 'YYYY-MM-ddTHH:mm:ss',
    DateFormat: 'MMM d y',
    DateTimeFormat: 'MMM d y HH:mm',
    DateTimeNumericFormat: 'MM/dd/yyyy hh:mm a',
    DateTimeFormatMidDay: 'MMM d y HH:mm a',
    DateShortFormat: 'MM-dd-YYYY',
    DayFormat: 'EEE',
    TimeFormat: 'hh:mm',
    TimeFormatMidDay: 'hh:mm a',
    TimeFormatHourOnly: 'h a',
    TimeFormatHourOnlyToShow: 'HH a',
    DateFormatEEE: 'EEE MMM d y',
    DayDateFormat: 'MMM d'
  },
  Table: { elapsedTimeBeforeEmptyMessage: 4000 },
  ScheduleStatus: { active: 'status-active', inActive: 'status-inactive' },
  Schedule: {
    defaultBulkType: BulkType.EverySlot,
    // @todo Once we have defined API mapping describing all states
    // (why student can't register for particular activity this should be removed)
    showActivityNAMessage: false
  },
  DateManagerService: {
    maxDateYears: 50
  },
  DefaultCalendarViewTabRoleMapping: {
    [Authority.Student]: CalendarViewTab.Day,
    [Authority.Admin]: CalendarViewTab.Week,
    [Authority.Staff]: CalendarViewTab.Week,
    [Authority.Teacher]: CalendarViewTab.Week,
    [Authority.Substitute]: CalendarViewTab.Week
  } as Record<Authority, CalendarViewTab>,
  DefaultRouteUserRoleMapping: {
    [Authority.Student]: AppRoute.Schedule,
    [Authority.Admin]: AppRoute.Dashboard,
    [Authority.Staff]: AppRoute.Schedule,
    [Authority.Teacher]: AppRoute.Schedule,
    [Authority.Substitute]: AppRoute.StudentAttendance
  } as Record<Authority, AppRoute>,
  DateTimePickers: {
    dateTimeRelatedStepMin: 15,
    dateTimeStepMin: 1,
    dateTimeAttendanceReminderStepMin: 5,
    defaultStartTimeSec: 7 * 60 * 60,
    defaultEndTimeSec: 7 * 60 * 60 + 15 * 60,
    filterDefaultEndDateHours: 24,
    countWeekends: false
  },
  BsConfig: {
    adaptivePosition: true,
    showClearButton: true,
    clearPosition: 'center',
    dateInputFormat: 'MM-DD-YYYY',
    daysDisabled: [6, 0],
    datesDisabled: [] as Date[]
  },
  SchoolYearResetCalendarConfig: {
    allowFromStartMonth: 2, // March
    allowToEndMonth: 5,     // June
    allowStartDay: 1,       // 1st of March
    allowToDay: 30,         // 30th of June
    rangeFromMonth: 6,      // July
    rangeToMonth: 8,        // September
    rangeFromDay: 2,        // 2 July
    rangeToDay: 30          // 30 September
  },
  EditorConfig: {
    minHeight: '200px',
    editable: true,
    enableToolbar: true,
    showToolbar: true,
    sanitize: true,
    customClasses: [
      {
        name: "quote",
        class: "quote",
      }],
    toolbarHiddenButtons: [
      ['fontName'],
      [
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'insertImage',
        'unlink',
        'insertVideo',
        'insertHorizontalRule',
        'removeFormat',
        'toggleEditorMode'
      ]
    ]
  },
  CalendarViewTabFormatMapping: {
    [CalendarViewTab.Day]: ['EEE', 'MMM d y', ''],
    [CalendarViewTab.Week]: ['W', 'MMM y', 'WEEK'],
    [CalendarViewTab.TodayPlus]: ['W', 'MMM y', 'WEEK'],
    [CalendarViewTab.Month]: ['MMM', 'y', '']
  } as Record<CalendarViewTab, string[]>,
  PriorityTypeClassMapping: {
    [ActivityPriorityType.AdminMustAttend]: 'priority-ama',
    [ActivityPriorityType.PriorityRoster]: 'priority-pr',
    [ActivityPriorityType.TeacherRequested]: 'priority-tr',
    [ActivityPriorityType.RegularRoster]: 'priority-rr',
    [ActivityPriorityType.OptionalRoster]: 'priority-or',
    [ActivityPriorityType.AdvertiseActivity]: 'priority-aa',
    [ActivityPriorityType.NormalActivity]: 'priority-na'
  } as Record<ActivityPriorityType, string>,
  PriorityTypeColorMapping: {
    [ActivityPriorityType.AdminMustAttend]: '#DCBA07',
    [ActivityPriorityType.PriorityRoster]: '#847CF3',
    [ActivityPriorityType.TeacherRequested]: '#308DEB',
    /* WCAG 1.4.11 — chart segments vs white ≥3:1 (was #D4D0FA ~1.5:1) */
    [ActivityPriorityType.RegularRoster]: '#8D87D4',
    [ActivityPriorityType.OptionalRoster]: '#D96D04',
    [ActivityPriorityType.AdvertiseActivity]: '#379B7E',
    /* WCAG 1.4.11 — was #92A7BA ~2.5:1 */
    [ActivityPriorityType.NormalActivity]: '#7F97AE'
  } as Record<ActivityPriorityType, string>,
  StudentAttendanceTypeClassTransMapping: {
    [StudentAttendanceType.Absent]: ['danger', 'ABSENT', 'ftmi-cross'],
    [StudentAttendanceType.Present]: ['success', 'PRESENT', 'ftmi-check'],
    [StudentAttendanceType.LateUnexcused]: ['warning', 'LATE_UNEXCUSED', 'ftmi-check'],
    [StudentAttendanceType.LateExcused]: ['warning', 'LATE_EXCUSED', 'ftmi-check']
  } as Record<StudentAttendanceType, [string, string, string]>,
  ColumnNameWidthMapping: {
    actions: 200,
    controlledAccessPeriod: 90,
    cutoffDays: 105,
    cutoffTime: 100,
    date: 120,
    startDate: 100,
    endDate: 100,
    startTime: 100,
    endTime: 100,
    daysRemaining: 90,
    defaultSubstitutePlan: 220,
    email: 250,
    graduationYear: 100,
    maxAttendees: 100,
    membershipCount: 90,
    priority: 90,
    status: 80,
    scheduled: 90,
    submit: 60,
    ['role.code']: 90,
    ['user.email']: 250
  } as Record<string, number>,
  ChartColorLabelClassesMapping: {
    '#7f97ae': 'typeNormal',
    '#379b7e': 'typeAdvertise',
    '#d96d04': 'typeOptionalRoster',
    '#8d87d4': 'typeRegularRoster',
    '#847cf3': 'typePriorityRoster',
    '#dcba07': 'typeAMA',
    '#f24c42': 'typeWithoutActivities',
    '#909c0f': 'typeWithActivities',
    '#ffcb8d': 'typeExcluded',
    '#dc765e': 'typeNotRegistered',
    '#858f0d': 'typeRegistered',
    '#2a9ece': 'typeAttendancePending',
    '#00a2ff': 'typeAttendanceTaken',
    '#ffcc90': 'typeStudentLateExcused',
    '#feaf96': 'typeStudentLateUnexcused'
  } as Record<string, string>,
  MaxSharedAttendance: 5,
  FormControls: {
    defaultStatusValue: Status.Active
  },
  Bootstrap: {
    accordionGroup: {
      isOpen: true
    }
  },
  CutoffTime: {
    cutoffMinutesShiftBeforeStartTime: 1
  },
  QRCode: { smallScreen: { size: 256, fps: 10 }, largeScreen: { size: 500, fps: 10 } },
  MetricColorTranslationMapping: {
    countWithoutActivities: {
      translationkey: 'Without activities',
      backgroundColor: '#f24c42'
    },
    countWithActivities: {
      translationkey: 'With Activities',
      backgroundColor: '#909c0f'
    },
    countExcluded: {
      translationkey: 'Excluded',
      // WCAG 1.4.11 (Deque 2159536) — #ffcb8d was 1.5:1 on white (fail). #8c5a00 ≈ 6.1:1 on white (pass).
      backgroundColor: '#8c5a00'
    },
    countNotRegistered: {
      translationkey: 'Not Registered',
      backgroundColor: '#dc765e'
    },
    countRegistered: {
      translationkey: 'Registered',
      backgroundColor: '#858f0d'
    },
    countAttendancePending: {
      translationkey: 'Attendance Pending',
      backgroundColor: '#2a9ece'
    },
    countAttendanceTaken: {
      translationkey: 'Attendance Taken',
      // WCAG 1.4.11 (Deque 2159536) — #00a2ff was 2.7:1 on white (fail). #0068b3 ≈ 5.8:1 on white (pass).
      backgroundColor: '#0068b3'
    },
    countPresent: {
      translationkey: 'Present',
      backgroundColor: '#909c0f'
    },
    countAbsent: {
      translationkey: 'Absent',
      backgroundColor: '#F24C42'
    },
    countLateExcused: {
      translationkey: 'Late Excused',
      // WCAG 1.4.11 (Deque 2159536) — #FFCB8D was 1.5:1 on white (fail). #8c5a00 ≈ 6.1:1 on white (pass).
      backgroundColor: '#8c5a00'
    },
    countLateUnexcused: {
      translationkey: 'Late Unexcused',
      backgroundColor: '#dc765e'
    }
  } as Record<string, { translationkey: string; backgroundColor: string }>,
  ChartOptions: {
    responsive: true,
    aspectRatio: 1,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        display: false,
        position: 'chartArea',
        labels: {
          boxWidth: 15,
          /* WCAG 1.4.3: ≥4.5:1 on white (was #66738f, borderline) */
          color: '#384d61',
          font: { family: '"Open Sans", sans-serif' },
          padding: 2
        }
      },
  
      tooltip: {
        enabled: false,
        external: (context: any) => {
          let tooltip: any = document.getElementById('chartjs-tooltip');
      
          // Create tooltip once
          if (!tooltip) {
            tooltip = document.createElement('div');
            tooltip.id = 'chartjs-tooltip';
      
            tooltip.innerHTML = `
              <div style="font-weight:600;font-size:14px;" class="tooltip-title"></div>
              <div style="display:flex;align-items:center">
                <span class="tooltip-color"></span>
                <span class="tooltip-value"></span>
              </div>
              <div class="tooltip-arrow"></div>
            `;
      
            tooltip.style.background = 'rgba(47, 47, 47, 0.9)';
            tooltip.style.borderRadius = '5px';
            tooltip.style.padding = '10px';
            tooltip.style.position = 'absolute';
            tooltip.style.opacity = '0';
            tooltip.style.color = '#ffffff';
            tooltip.style.zIndex = '9999';
      
            // ✅ WIDTH ONLY (as requested)
            tooltip.style.minWidth = '100px';
            tooltip.style.maxWidth = '260px';
      
            // Arrow
            const arrow = tooltip.querySelector('.tooltip-arrow');
            arrow.style.position = 'absolute';
            arrow.style.left = '50%';
            arrow.style.bottom = '-6px';
            arrow.style.transform = 'translateX(-50%)';
            arrow.style.borderLeft = '6px solid transparent';
            arrow.style.borderRight = '6px solid transparent';
            arrow.style.borderTop = '6px solid rgba(47, 47, 47, 0.9)';
      
            document.body.appendChild(tooltip);
            attachChartTooltipWcag1413(tooltip);
          }
      
          const tooltipModel = context.tooltip;
      
          // Hide (WCAG 1.4.13: defer hide so pointer can move onto #chartjs-tooltip)
          if (tooltipModel.opacity === 0) {
            chartTooltipApplyChartHide(tooltip);
            return;
          }
      
          // ✅ FIX: Correct label & value
          const dataPoint = tooltipModel.dataPoints?.[0];
          if (!dataPoint) {
            chartTooltipHideImmediately(tooltip);
            return;
          }

          const chart = (context as { chart?: { canvas?: HTMLCanvasElement } }).chart;
          chartTooltipOnShown(tooltip, chart?.canvas ?? null);

          tooltip.style.opacity = '1';
      
          const label = dataPoint.label;                 // "Without activities"
          const value = dataPoint.formattedValue;        // "54"
          const color = dataPoint.dataset.backgroundColor[dataPoint.dataIndex];
      
          tooltip.querySelector('.tooltip-title').innerText = label;
          tooltip.querySelector('.tooltip-value').innerText = value;
      
          const colorBox = tooltip.querySelector('.tooltip-color');
          colorBox.style.width = '14px';
          colorBox.style.height = '14px';
          colorBox.style.background = color;
          colorBox.style.marginRight = '8px';
          colorBox.style.borderRadius = '2px';
          colorBox.style.display = 'inline-block';
      
          // ✅ Position tooltip on top of chart (centered horizontally)
          const chartCanvas = chart?.canvas;
          if (chartCanvas) {
            // Use requestAnimationFrame to ensure tooltip dimensions are calculated
            requestAnimationFrame(() => {
              const chartRect = chartCanvas.getBoundingClientRect();
              const tooltipRect = tooltip.getBoundingClientRect();
              
              // Get scroll offsets
              const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
              const scrollY = window.pageYOffset || document.documentElement.scrollTop;
              
              // Position at top center of chart (relative to document)
              const left = chartRect.left + scrollX + (chartRect.width / 2) - (tooltipRect.width / 2);
              const top = chartRect.top + scrollY - tooltipRect.height - 15; // 15px above chart
              
              tooltip.style.left = Math.max(10, left) + 'px'; // Ensure it doesn't go off left edge
              tooltip.style.top = Math.max(10, top) + 'px'; // Ensure it doesn't go off top edge
              
              // Update arrow position to point down (since tooltip is above)
              const arrow = tooltip.querySelector('.tooltip-arrow');
              if (arrow) {
                arrow.style.top = '100%';
                arrow.style.bottom = 'auto';
                arrow.style.borderTop = '6px solid rgba(47, 47, 47, 0.9)';
                arrow.style.borderBottom = 'none';
              }
            });
          }
        }
      }
    }
  } as ChartOptions as ChartOptions,
  ChartType: 'doughnut' as ChartType,
  SchoolYear: {
    StartYear: 2023,
    StartMonth: 6,
    StartDate: 1
  }
};
